import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './UserInfoModal.css';
import { apiClient, isApiError } from '../../../lib/apiClient';
import {
  useUser,
  getCognitoAuthSnapshot,
  getCognitoUserEmail,
  getCognitoUserGroups,
} from '../../../context/UserContext';
import {
  useDivisionOptions,
  useOrgMastersRefresher,
  useTeamOptions,
} from '../../../state/master';
import { useUserProfile } from '../../../context/UserProfileContext';
import { DatalistProvider } from '../Datalist/DatalistProvider';
import PublishScopeDialog from '../PublishScopeDialog/PublishScopeDialog';
import { notifyAccessContextUpdated } from '../../../lib/accessContextUpdate';
import { notifyUserRegistered } from '../../../lib/userRegistrationEvent';
import { shareInFlightRequest } from '../../../lib/sharedApiRequest';
import { ENV } from '../../../constants';

export type DivisionOption = {
  value: string;
  label: string;
  department_id?: number;
};

export type TeamOption = {
  value: string;
  label: string;
  team_id?: number;
  department_id?: number;
};

type TeamOptionForSelect = TeamOption & {
  temporaryNewDepartmentTeamId?: string;
  isTemporaryNewDepartmentTeam?: boolean;
};

export type UserInfoApiResponse = {
  department?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  last_name_kana?: string | null;
  lastNameKana?: string | null;
  first_name_kana?: string | null;
  firstNameKana?: string | null;
  team?: string | null;
  preferred_team_id?: string | number | null;
  preferredTeamId?: string | number | null;
  preferred_team_label?: string | null;
  preferredTeamLabel?: string | null;
  preferred_team_updated_at?: string | null;
  preferredTeamUpdatedAt?: string | null;
  public?: any;
  auth?: any[];
  rbac_endpoint?: any;
  [key: string]: any;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  divisions?: DivisionOption[];
  teams?: TeamOption[];
  title?: string;
  // true の場合、送信先を /update_user_info に切替
  forceUpdateEndpoint?: boolean;
  // 編集モード（既存プロフィール編集用）
  editMode?: boolean;
  lockUntilSaved?: boolean;
  initialUserInfo?: UserInfoApiResponse | null;
  onAllowCancelAfterSubmitFailure?: () => void;
};

// ひらがなバリデーション用（ぁ〜ゖ と長音符・スペース）
const HIRAGANA_REGEX = /^[\u3041-\u3096\u30fc\s]+$/;
const normText = (v: unknown): string =>
  String(v ?? '').normalize('NFKC').trim();
const tokenText = (v: unknown): string =>
  normText(v).replace(/\s+/g, '').toUpperCase();
const dedupeTeamsByLabel = (list: TeamOption[]): TeamOption[] => {
  const seen = new Set<string>();
  const out: TeamOption[] = [];
  for (const opt of list) {
    const key = tokenText(opt.label ?? opt.value);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(opt);
  }
  return out;
};
const hasValidPreferredTeamId = (raw: unknown): boolean => {
  const source = String(raw ?? '').trim();
  if (!source) return false;
  const teamId = Number(source);
  return Number.isFinite(teamId) && teamId > 0;
};

const NEW_DEPARTMENT_TEMP_TEAM_OPTIONS: TeamOptionForSelect[] = [
  {
    value: 'XX-3 XX\u30c1\u30fc\u30e0',
    label: 'XX-3 XX\u30c1\u30fc\u30e0',
    temporaryNewDepartmentTeamId: '__new_department_temp_team_xx__',
    isTemporaryNewDepartmentTeam: true,
  },
  {
    value: 'YY-3 YY\u30c1\u30fc\u30e0',
    label: 'YY-3 YY\u30c1\u30fc\u30e0',
    temporaryNewDepartmentTeamId: '__new_department_temp_team_yy__',
    isTemporaryNewDepartmentTeam: true,
  },
];

const getTeamOptionSelectValue = (opt: TeamOptionForSelect): string => {
  const temporaryId = String(opt.temporaryNewDepartmentTeamId ?? '').trim();
  if (temporaryId) return temporaryId;
  return String((opt as any)?.team_id ?? '').trim();
};

const parseRbacEndpoint = (
  raw: unknown,
): { available: boolean; isAdmin: boolean } => {
  if (raw == null) return { available: false, isAdmin: false };

  let parsed: any = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { available: false, isAdmin: false };
    }
  }

  const payload = parsed?.response ?? parsed;
  if (!Array.isArray(payload?.data)) {
    return { available: false, isAdmin: false };
  }
  const users = payload.data;
  const isAdmin = users.some((u: any) => {
    let teams: any[] = [];
    if (Array.isArray(u?.teams)) {
      teams = u.teams;
    } else if (typeof u?.teams === 'string') {
      try {
        const parsedTeams = JSON.parse(u.teams);
        teams = Array.isArray(parsedTeams) ? parsedTeams : [];
      } catch {
        teams = [];
      }
    }

    return teams.some((t: any) => {
      const role = String(t?.role ?? '').toLowerCase();
      return role === 'admin' || role === 'rep';
    });
  });
  return { available: true, isAdmin };
};

const resolveAuthority = (
  data: any,
  cognitoGroupIsAdmin: boolean,
): number | null => {
  const hasUserAuthManageEntry =
    Array.isArray(data?.auth) && data.auth.length > 0;

  // ローカル環境（REACT_APP_APPLICATION_VERSION=Local）では、USER_AUTH_MANAGE に
  // 対象メールアドレスの登録があれば、RBAC/Cognitoグループより優先してその値を使う
  if (ENV.IS_LOCAL_VERSION && hasUserAuthManageEntry) {
    return Math.max(...data.auth);
  }

  const rbac = parseRbacEndpoint(data?.rbac_endpoint);
  let authorityVal: number | null = null;

  if (rbac.available) {
    authorityVal = rbac.isAdmin ? 1 : null;
  } else {
    authorityVal = cognitoGroupIsAdmin ? 1 : null;
  }

  if (authorityVal !== 1 && hasUserAuthManageEntry) {
    authorityVal = Math.max(...data.auth);
  }

  return authorityVal;
};

type DraftPublicState = {
  private_flg: number;
  public_department: string | null;
  public_team: string | null;
};

const buildDraftPublicFromUserInfo = (
  data: UserInfoApiResponse | null | undefined,
): DraftPublicState => {
  const pub = (data as any)?.public;
  const nextPub: DraftPublicState = {
    private_flg: 0,
    public_department: null,
    public_team: null,
  };
  if (!pub) return nextPub;

  const p = Number((pub as any)?.private_flg);
  nextPub.private_flg = p === 1 ? 1 : p === 2 ? 2 : 0;
  const depStr = String((pub as any)?.public_department ?? '').trim();
  nextPub.public_department = depStr !== '' ? depStr : null;
  const teamStr = String((pub as any)?.public_team ?? '').trim();
  nextPub.public_team = teamStr !== '' ? teamStr : null;
  return nextPub;
};

export const UserInfoModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSaved,
  divisions = [],
  teams = [],
  title = '自分の情報を設定してください',
  forceUpdateEndpoint = false,
  editMode = false,
  lockUntilSaved = false,
  initialUserInfo = null,
  onAllowCancelAfterSubmitFailure,
}) => {
  const [division, setDivision] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastNameKana, setLastNameKana] = useState<string>('');
  const [firstNameKana, setFirstNameKana] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [allowCancelAfterSubmitFailure, setAllowCancelAfterSubmitFailure] =
    useState<boolean>(false);

  // 下書きの他課公開（モーダル内のみで保持するドラフト）
  const [draftPublic, setDraftPublic] = useState<DraftPublicState>({
    private_flg: 1,
    public_department: null,
    public_team: null,
  });
  const [publishDialogOpen, setPublishDialogOpen] = useState<boolean>(false);

  // 表示ラベル（公開/非公開/一部公開（...））
  const draftPublishLabel = React.useMemo(() => {
    const priv = Number((draftPublic as any)?.private_flg);
    if (priv === 1) return '非公開';
    if (priv === 2) {
      const dep = String((draftPublic as any)?.public_department ?? '').trim();
      return dep ? `一部公開（${dep}）` : '一部公開';
    }
    return '公開';
  }, [draftPublic]);

  const { profile: userProfile, setProfile } = useUserProfile();
  const { refreshOrgMastersOnly } = useOrgMastersRefresher();
  const [preferredTeamId, setPreferredTeamId] = useState<string>('');
  const [preferredTeamHints, setPreferredTeamHints] = useState<{
    preferredTeamId?: string | number | null;
    preferredTeamLabel?: string | null;
  } | null>(null);
  const profileSnapshotAppliedForOpenRef = useRef(false);
  const formEditRevisionRef = useRef(0);
  const userInfoLoadRequestIdRef = useRef(0);

  const markFormEdited = useCallback(() => {
    formEditRevisionRef.current += 1;
  }, []);

  const setDivisionFromUser = useCallback<React.Dispatch<React.SetStateAction<string>>>(
    (update) => {
      const nextDivision =
        typeof update === 'function'
          ? update(division)
          : update;

      if (nextDivision !== division) {
        markFormEdited();
        setPreferredTeamHints(null);
        setPreferredTeamId('');
      }
      setDivision(nextDivision);
    },
    [division, markFormEdited],
  );

  const sortedTeamOptions = useMemo<TeamOption[]>(() => {
    const list = Array.isArray(teams) ? teams : [];
    return [...list].sort((a, b) =>
      normText(a.label ?? a.value).localeCompare(normText(b.label ?? b.value), 'ja'),
    );
  }, [teams]);

  const selectedDepartmentOption = useMemo<DivisionOption | null>(() => {
    const divisionText = normText(division);
    if (!divisionText) return null;
    return (divisions ?? []).find(
      (d) =>
        tokenText(d.label) === tokenText(divisionText) ||
        tokenText(d.value) === tokenText(divisionText),
    ) ?? null;
  }, [division, divisions]);

  const selectedDepartmentId = useMemo<number | null>(() => {
    const deptId = Number((selectedDepartmentOption as any)?.department_id);
    return Number.isFinite(deptId) && deptId > 0 ? deptId : null;
  }, [selectedDepartmentOption]);

  const isNewDepartmentInput = useMemo(
    () => Boolean(normText(division)) && selectedDepartmentOption == null,
    [division, selectedDepartmentOption],
  );

  const divisionTeamOptions = useMemo<TeamOptionForSelect[]>(() => {
    if (isNewDepartmentInput) return NEW_DEPARTMENT_TEMP_TEAM_OPTIONS;
    if (selectedDepartmentId == null) return dedupeTeamsByLabel(sortedTeamOptions);
    const filtered = sortedTeamOptions.filter(
      (t) => Number((t as any)?.department_id) === selectedDepartmentId,
    );
    return filtered;
  }, [isNewDepartmentInput, sortedTeamOptions, selectedDepartmentId]);

  const resolveInitialPreferredTeamId = useCallback(
    (hints?: {
      preferredTeamId?: string | number | null;
      preferredTeamLabel?: string | null;
    }): string => {
      const preferredId = String(
        hints?.preferredTeamId ?? userProfile?.preferredTeamId ?? '',
      ).trim();
      if (preferredId) {
        const byId = sortedTeamOptions.find(
          (t) => String((t as any)?.team_id ?? '') === preferredId,
        );
        if (byId) return String((byId as any)?.team_id ?? '');
        if (hasValidPreferredTeamId(preferredId)) return preferredId;
      }

      const preferredLabel = String(
        hints?.preferredTeamLabel ?? userProfile?.preferredTeamLabel ?? '',
      ).trim();
      if (preferredLabel) {
        const targetToken = tokenText(preferredLabel);
        const byLabel = sortedTeamOptions.find(
          (t) => tokenText(t.label ?? t.value) === targetToken,
        );
        if (byLabel) return String((byLabel as any)?.team_id ?? '');
      }

      return '';
    },
    [
      userProfile?.preferredTeamId,
      userProfile?.preferredTeamLabel,
      sortedTeamOptions,
    ],
  );

  const selectedPreferredTeam = useMemo<TeamOptionForSelect | null>(
    () => {
      if (isNewDepartmentInput) {
        return NEW_DEPARTMENT_TEMP_TEAM_OPTIONS.find(
          (t) => getTeamOptionSelectValue(t) === String(preferredTeamId),
        ) ?? null;
      }

      return sortedTeamOptions.find(
        (t) => String((t as any)?.team_id ?? '') === String(preferredTeamId),
      ) ?? null;
    },
    [isNewDepartmentInput, sortedTeamOptions, preferredTeamId],
  );

  const visibleTeamOptions = useMemo<TeamOptionForSelect[]>(
    () => divisionTeamOptions,
    [divisionTeamOptions],
  );

  const selectedPreferredTeamDeptId = useMemo<number | null>(() => {
    const deptId = Number((selectedPreferredTeam as any)?.department_id ?? 0);
    return Number.isFinite(deptId) && deptId > 0 ? deptId : null;
  }, [selectedPreferredTeam]);

  const selectedPreferredTeamHasValidId = useMemo(
    () =>
      Boolean(
        selectedPreferredTeam &&
          (selectedPreferredTeam.isTemporaryNewDepartmentTeam ||
            hasValidPreferredTeamId((selectedPreferredTeam as any)?.team_id)),
      ),
    [selectedPreferredTeam],
  );

  const selectedPreferredTeamIsTemporary = Boolean(
    selectedPreferredTeam?.isTemporaryNewDepartmentTeam,
  );

  const isPreferredTeamDeptMismatch = Boolean(
    selectedPreferredTeam &&
      !selectedPreferredTeamIsTemporary &&
      selectedDepartmentId != null &&
      selectedPreferredTeamDeptId != null &&
      selectedPreferredTeamDeptId !== selectedDepartmentId,
  );

  const teamHelperText = useMemo(() => {
    if (!preferredTeamId) return '自分のチームを選択してください。';
    if (!selectedPreferredTeamHasValidId) return '有効なチームを選択してください。';
    if (isPreferredTeamDeptMismatch) {
      return '課とチームの組み合わせが不正です。チームを選び直してください。';
    }
    return '';
  }, [
    preferredTeamId,
    selectedPreferredTeamHasValidId,
    isPreferredTeamDeptMismatch,
  ]);

  const canSubmit = useMemo(
    () =>
      !submitting &&
      Boolean(normText(division)) &&
      Boolean(normText(lastName)) &&
      Boolean(normText(firstName)) &&
      selectedPreferredTeamHasValidId &&
      !isPreferredTeamDeptMismatch,
    [
      submitting,
      division,
      lastName,
      firstName,
      selectedPreferredTeamHasValidId,
      isPreferredTeamDeptMismatch,
    ],
  );

  // 課の選択に応じて、自分のチームの選択肢を整合させる
  useEffect(() => {
    if (!preferredTeamId) return;
    if (isNewDepartmentInput) {
      const hit = NEW_DEPARTMENT_TEMP_TEAM_OPTIONS.find(
        (t) => getTeamOptionSelectValue(t) === String(preferredTeamId),
      );
      if (!hit) setPreferredTeamId('');
      return;
    }
    if (selectedDepartmentId == null) return;
    if (sortedTeamOptions.length === 0) return;

    const hit = sortedTeamOptions.find(
      (t) => String((t as any)?.team_id ?? '') === String(preferredTeamId),
    );
    if (!hit) {
      setPreferredTeamId('');
      return;
    }
    const teamDeptId = Number((hit as any)?.department_id ?? 0);
    if (Number.isFinite(teamDeptId) && teamDeptId > 0 && teamDeptId !== selectedDepartmentId) {
      setPreferredTeamId('');
    }
  }, [preferredTeamId, isNewDepartmentInput, selectedDepartmentId, sortedTeamOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (!preferredTeamHints) return;
    if (sortedTeamOptions.length === 0) return;

    const nextPreferredTeamId = resolveInitialPreferredTeamId(preferredTeamHints);
    setPreferredTeamHints(null);
    if (nextPreferredTeamId && nextPreferredTeamId !== preferredTeamId) {
      setPreferredTeamId(nextPreferredTeamId);
    }
  }, [
    isOpen,
    preferredTeamHints,
    preferredTeamId,
    resolveInitialPreferredTeamId,
    sortedTeamOptions.length,
  ]);

  const applyProfileSnapshotToForm = useCallback(() => {
    setDivision(String(userProfile?.department ?? '').trim());
    setLastName(String(userProfile?.lastName ?? '').trim());
    setFirstName(String(userProfile?.firstName ?? '').trim());
    setLastNameKana(String(userProfile?.lastNameKana ?? '').trim());
    setFirstNameKana(String(userProfile?.firstNameKana ?? '').trim());
    const hints = {
      preferredTeamId: userProfile?.preferredTeamId ?? null,
      preferredTeamLabel: userProfile?.preferredTeamLabel ?? null,
    };
    setPreferredTeamHints(hints);
    setPreferredTeamId(resolveInitialPreferredTeamId(hints));
  }, [
    resolveInitialPreferredTeamId,
    userProfile?.department,
    userProfile?.firstName,
    userProfile?.firstNameKana,
    userProfile?.lastName,
    userProfile?.lastNameKana,
    userProfile?.preferredTeamId,
    userProfile?.preferredTeamLabel,
  ]);

  const applyProfileFallback = useCallback(() => {
    applyProfileSnapshotToForm();
  }, [applyProfileSnapshotToForm]);

  const applyUserInfoToForm = useCallback(
    (data: UserInfoApiResponse) => {
      const division2 = String(
        data?.department ?? userProfile?.department ?? '',
      ).trim();
      const lastName2 = String(
        data?.last_name ?? data?.lastName ?? userProfile?.lastName ?? '',
      ).trim();
      const firstName2 = String(
        data?.first_name ?? data?.firstName ?? userProfile?.firstName ?? '',
      ).trim();
      const lastNameKana2 = String(
        data?.last_name_kana ?? data?.lastNameKana ?? '',
      ).trim();
      const firstNameKana2 = String(
        data?.first_name_kana ?? data?.firstNameKana ?? '',
      ).trim();
      const hints = {
        preferredTeamId: data?.preferred_team_id ?? data?.preferredTeamId ?? null,
        preferredTeamLabel:
          data?.preferred_team_label ?? data?.preferredTeamLabel ?? null,
      };

      setDivision(division2);
      setLastName(lastName2);
      setFirstName(firstName2);
      setLastNameKana(lastNameKana2);
      setFirstNameKana(firstNameKana2);
      setPreferredTeamHints(hints);
      setPreferredTeamId(resolveInitialPreferredTeamId(hints));
      setDraftPublic(buildDraftPublicFromUserInfo(data));
    },
    [
      resolveInitialPreferredTeamId,
      userProfile?.department,
      userProfile?.firstName,
      userProfile?.lastName,
    ],
  );

  // Apply the profile updated by the previous save before the browser paints.
  // The request below still revalidates the form with the latest server value.
  useLayoutEffect(() => {
    if (!isOpen) {
      profileSnapshotAppliedForOpenRef.current = false;
      return;
    }
    if (
      initialUserInfo ||
      (!editMode && !forceUpdateEndpoint) ||
      profileSnapshotAppliedForOpenRef.current
    ) {
      return;
    }

    profileSnapshotAppliedForOpenRef.current = true;
    applyProfileSnapshotToForm();
  }, [
    applyProfileSnapshotToForm,
    editMode,
    forceUpdateEndpoint,
    initialUserInfo,
    isOpen,
  ]);

  // モーダルオープン時に DB の値（ふりがな含む）を読み込む
  useEffect(() => {
    const requestId = ++userInfoLoadRequestIdRef.current;
    const formEditRevisionAtRequest = formEditRevisionRef.current;
    const canApplyLoadResult = () =>
      requestId === userInfoLoadRequestIdRef.current &&
      formEditRevisionAtRequest === formEditRevisionRef.current;

    if (!isOpen) return;

    setError('');
    setAllowCancelAfterSubmitFailure(false);

    if (initialUserInfo) {
      applyUserInfoToForm(initialUserInfo);
      return;
    }

    if (editMode || forceUpdateEndpoint) {
      // 既存ユーザー編集時や update 強制時は、DB から最新の情報を取得して反映する
      (async () => {
        try {
          const mail = await getCognitoUserEmail();
          if (!mail) {
            if (canApplyLoadResult()) applyProfileFallback();
            return;
          }

          const res = await shareInFlightRequest(
            `get-user-info:${mail.trim().toLowerCase()}`,
            () => apiClient.post('/get_user_info', { mail_address: mail }),
          );

          if (res?.status === 200) {
            if (!canApplyLoadResult()) return;
            const data: any = res.data;
            applyUserInfoToForm(data);

            const division2 = String(
              data?.department ?? userProfile?.department ?? '',
            ).trim();
            const lastName2 = String(
              data?.last_name ??
                data?.lastName ??
                userProfile?.lastName ??
                '',
            ).trim();
            const firstName2 = String(
              data?.first_name ??
                data?.firstName ??
                userProfile?.firstName ??
                '',
            ).trim();
            const lastNameKana2 = String(
              data?.last_name_kana ?? data?.lastNameKana ?? '',
            ).trim();
            const firstNameKana2 = String(
              data?.first_name_kana ?? data?.firstNameKana ?? '',
            ).trim();

            setDivision(division2);
            setLastName(lastName2);
            setFirstName(firstName2);
            setLastNameKana(lastNameKana2);
            setFirstNameKana(firstNameKana2);
            setPreferredTeamId(
              resolveInitialPreferredTeamId({
                preferredTeamId:
                  data?.preferred_team_id ?? data?.preferredTeamId ?? null,
                preferredTeamLabel:
                  data?.preferred_team_label ?? data?.preferredTeamLabel ?? null,
              }),
            );

            // /get_user_info の public を反映
            try {
              const pub = (data as any)?.public;
              const nextPub = {
                private_flg: 0,
                public_department: null as string | null,
                public_team: null as string | null,
              };
              if (pub) {
                const p = Number((pub as any)?.private_flg);
                nextPub.private_flg = p === 1 ? 1 : (p === 2 ? 2 : 0);
                const depRaw = (pub as any)?.public_department;
                const depStr = depRaw == null ? '' : String(depRaw).trim();
                nextPub.public_department = depStr !== '' ? depStr : null;
                const teamRaw = (pub as any)?.public_team;
                const teamStr = teamRaw == null ? '' : String(teamRaw).trim();
                nextPub.public_team = teamStr !== '' ? teamStr : null;
              }
              setDraftPublic(nextPub);
            } catch {}
          } else {
            if (!canApplyLoadResult()) return;
            // 念のためフォールバック（profile の値だけ）
            setDivision(String(userProfile?.department ?? '').trim());
            setLastName(String(userProfile?.lastName ?? '').trim());
            setFirstName(String(userProfile?.firstName ?? '').trim());
            applyProfileFallback();
          }
        } catch {
          if (!canApplyLoadResult()) return;
          // 取得に失敗した場合も、最低限 profile の値だけは反映
          setDivision(String(userProfile?.department ?? '').trim());
          setLastName(String(userProfile?.lastName ?? '').trim());
          setFirstName(String(userProfile?.firstName ?? '').trim());
          applyProfileFallback();
        }
      })();
    } else {
      // 新規登録モード時は空から開始（公開設定はデフォルト: 非公開）
      setDivision('');
      setLastName('');
      setFirstName('');
      setLastNameKana('');
      setFirstNameKana('');
      setPreferredTeamHints(null);
      setPreferredTeamId(resolveInitialPreferredTeamId());
      setDraftPublic({
        private_flg: 1,
        public_department: null,
        public_team: null,
      });
    }
  // Fetch/apply only when the modal open mode or controller-provided data changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editMode, forceUpdateEndpoint, initialUserInfo]);

  const onClickOk = async () => {
    const d = String(division || '').trim();
    const ln = String(lastName || '').trim();
    const fn = String(firstName || '').trim();
    const lnk = String(lastNameKana || '').trim();
    const fnk = String(firstNameKana || '').trim();
    if (
      selectedPreferredTeam &&
      !selectedPreferredTeamIsTemporary &&
      selectedDepartmentId != null &&
      selectedPreferredTeamDeptId != null &&
      selectedPreferredTeamDeptId !== selectedDepartmentId
    ) {
      setError(
        '課・チームの組み合わせが不正です。チームを選び直してください。',
      );
      return;
    }

    const preferredTeamPayload = selectedPreferredTeam
      ? {
          teamId: String((selectedPreferredTeam as any)?.team_id ?? '') || null,
          teamLabel:
            normText(
              (selectedPreferredTeam as any)?.label ??
                (selectedPreferredTeam as any)?.value ??
                '',
            ) || null,
        }
      : null;
    const preferredTeamIdForApi = preferredTeamPayload?.teamId
      ? Number(preferredTeamPayload.teamId)
      : null;
    const normalizedPreferredTeamIdForApi =
      !selectedPreferredTeamIsTemporary &&
      hasValidPreferredTeamId(preferredTeamPayload?.teamId) &&
      Number.isFinite(preferredTeamIdForApi as number)
        ? (preferredTeamIdForApi as number)
        : null;
    const preferredTeamLabelForFallback =
      preferredTeamPayload?.teamLabel ?? null;

    if (!d || !ln || !fn) {
      setError('課・姓・名はすべて必須です。');
      return;
    }

    if (!selectedPreferredTeamIsTemporary && normalizedPreferredTeamIdForApi == null) {
      setError('自分のチームは必須です。選択してください。');
      return;
    }

    // ふりがなが入力されている場合は「ひらがなのみ」をチェック
    if (lnk && !HIRAGANA_REGEX.test(lnk)) {
      setError('姓のふりがなは、ひらがなのみで入力してください。');
      return;
    }
    if (fnk && !HIRAGANA_REGEX.test(fnk)) {
      setError('名のふりがなは、ひらがなのみで入力してください。');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const mail = await getCognitoUserEmail();
      const endpoint =
        editMode || forceUpdateEndpoint
          ? '/update_user_info'
          : '/add_user_info';
      const preferredTeamRequest = selectedPreferredTeamIsTemporary
        ? { preferred_team_label: preferredTeamLabelForFallback }
        : { preferred_team_id: normalizedPreferredTeamIdForApi };
      await apiClient.post(endpoint, {
        mail_address: mail,
        department: d,
        last_name: ln,
        first_name: fn,
        last_name_kana: lnk || null,
        first_name_kana: fnk || null,
        public: {
          private_flg: Number((draftPublic as any)?.private_flg) || 0,
          public_department: (draftPublic as any)?.public_department ?? null,
          public_team: (draftPublic as any)?.public_team ?? null,
        },
        ...preferredTeamRequest,
      });

      // マスタ選択肢（division / team のみ）を最新化
      await refreshOrgMastersOnly?.();

      // サーバー側に登録された最新のユーザー情報を再取得して反映（失敗時はフォールバック）
      let authorityVal: number | null = null;
      try {
        const groups = await getCognitoUserGroups();
        const cognitoGroupIsAdmin = groups.some((g) =>
          g.includes('-reps'),
        );
        authorityVal = cognitoGroupIsAdmin ? 1 : null;
        const res = await shareInFlightRequest(
          `get-user-info:${mail.trim().toLowerCase()}`,
          () => apiClient.post('/get_user_info', { mail_address: mail }),
        );
        if (res?.status === 200) {
          const data: any = res.data;
          const lastName2 = String(
            data?.last_name ?? data?.lastName ?? ln,
          ).trim();
          const firstName2 = String(
            data?.first_name ?? data?.firstName ?? fn,
          ).trim();
          const lastNameKana2 = String(
            data?.last_name_kana ?? data?.lastNameKana ?? lnk,
          ).trim();
          const firstNameKana2 = String(
            data?.first_name_kana ?? data?.firstNameKana ?? fnk,
          ).trim();
          const department2 = String(
            data?.department ?? d,
          ).trim();
          const team2 = String(data?.team ?? '').trim();
          authorityVal = resolveAuthority(data, cognitoGroupIsAdmin);

          // public の最新値を反映（モーダル内ドラフトも更新）
          try {
            const pub = (data as any)?.public;
            const nextPub = {
              private_flg: 0,
              public_department: null as string | null,
              public_team: null as string | null,
            };
            if (pub) {
              const p = Number((pub as any)?.private_flg);
              nextPub.private_flg = p === 1 ? 1 : (p === 2 ? 2 : 0);
              const depRaw = (pub as any)?.public_department;
              const depStr = depRaw == null ? '' : String(depRaw).trim();
              nextPub.public_department = depStr !== '' ? depStr : null;
              const teamRaw = (pub as any)?.public_team;
              const teamStr = teamRaw == null ? '' : String(teamRaw).trim();
              nextPub.public_team = teamStr !== '' ? teamStr : null;
            }
            setDraftPublic(nextPub);
          } catch {}

          setProfile({
            lastName: lastName2,
            firstName: firstName2,
            lastNameKana: lastNameKana2,
            firstNameKana: firstNameKana2,
            department: department2,
            team: team2,
            preferredTeamId: String(
              data?.preferred_team_id ?? data?.preferredTeamId ?? '',
            ).trim(),
            preferredTeamLabel: String(
              data?.preferred_team_label ?? data?.preferredTeamLabel ?? '',
            ).trim(),
            preferredTeamUpdatedAt: String(
              data?.preferred_team_updated_at ??
                data?.preferredTeamUpdatedAt ??
                '',
            ).trim(),
            authority: authorityVal,
          });
        } else {
          setProfile({
            lastName: ln,
            firstName: fn,
            lastNameKana: lnk,
            firstNameKana: fnk,
            department: d,
            team: '',
            preferredTeamId:
              normalizedPreferredTeamIdForApi != null
                ? String(normalizedPreferredTeamIdForApi)
                : '',
            preferredTeamLabel: String(preferredTeamLabelForFallback ?? ''),
            preferredTeamUpdatedAt:
              normalizedPreferredTeamIdForApi != null ||
              String(preferredTeamLabelForFallback ?? '').trim()
                ? new Date().toISOString()
                : '',
            authority: authorityVal,
          });
        }
      } catch {
        setProfile({
          lastName: ln,
          firstName: fn,
          lastNameKana: lnk,
          firstNameKana: fnk,
          department: d,
          team: '',
          preferredTeamId:
            normalizedPreferredTeamIdForApi != null
              ? String(normalizedPreferredTeamIdForApi)
              : '',
          preferredTeamLabel: String(preferredTeamLabelForFallback ?? ''),
          preferredTeamUpdatedAt:
            normalizedPreferredTeamIdForApi != null ||
            String(preferredTeamLabelForFallback ?? '').trim()
              ? new Date().toISOString()
              : '',
          authority: authorityVal,
        });
      }

      // 新しい課・チーム条件で、表示中の週報一覧と上書き検索を即時再検証する。
      notifyAccessContextUpdated();

      // 新規登録の場合のみ、描画済みの入力画面へ課・チームの初期選択のやり直しを通知する。
      if (!(editMode || forceUpdateEndpoint)) {
        notifyUserRegistered();
      }

      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } catch (e: any) {
      let message =
        'ユーザー情報の登録に失敗しました。時間をおいて再度お試しください。';
      if (isApiError && isApiError(e)) {
        const status = e?.response?.status;
        const respMsg =
          e?.response?.data?.message ??
          e?.response?.data?.detail?.message ??
          (typeof e?.response?.data?.detail === 'string'
            ? e.response.data.detail
            : '');
        const base = 'ユーザー情報の登録に失敗しました';
        if (status === 422) {
          setAllowCancelAfterSubmitFailure(true);
          onAllowCancelAfterSubmitFailure?.();
        }
        if (status) {
          message = `${base} (status: ${status}) ${respMsg}`.trim();
        } else if (respMsg) {
          message = `${base}。${respMsg}`;
        } else if (e?.message) {
          message = `${base}。${e.message}`;
        }
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  const showCancelButton =
    (editMode && !lockUntilSaved) || allowCancelAfterSubmitFailure;

  return (
    <div className="uimodal-backdrop" aria-modal="true" role="dialog">
      <div className="uimodal-container">
        <h2 className="uimodal-title">{title}</h2>

        <div className="uimodal-grid">
          <div className="uimodal-field">
            <label className="uimodal-label">課</label>
            <DatalistProvider
              valueState={[division, setDivisionFromUser]}
              initialOptions={(divisions ?? []).map((opt) =>
                String(opt.label || opt.value),
              )}
              halfAlnumOnly
              inputProps={{
                className: 'uimodal-input',
                placeholder: '選択または入力',
                required: true,
                'aria-required': true,
                'aria-label': '課',
                autoComplete: 'off',
              }}
            />
          </div>

          {/* 下書きの他課公開（リンク押下で PublishScopeDialog を表示） */}
          <div className="uimodal-field">
            <label className="uimodal-label">下書きの他課公開</label>
            <button
              type="button"
              onClick={() => setPublishDialogOpen(true)}
              className="uimodal-link"
              aria-label="下書きの他課公開を設定"
              style={{ padding: 4 }}
            >
              {draftPublishLabel}
            </button>
          </div>
        </div>

        <div className="uimodal-grid">
          <div className="uimodal-field">
            <label className="uimodal-label">チーム</label>
            <select
              className="uimodal-input"
              value={preferredTeamId}
              aria-label="チーム"
              onChange={(e) => {
                markFormEdited();
                setPreferredTeamHints(null);
                setPreferredTeamId(String(e.target.value || ''));
              }}
              required
              aria-required="true"
              aria-describedby={teamHelperText ? 'preferred-team-helper' : undefined}
            >
              <option value="">{'\u9078\u629e\u3057\u3066\u304f\u3060\u3055\u3044'}</option>
              {visibleTeamOptions.map((opt) => {
                const id = getTeamOptionSelectValue(opt);
                const label = String((opt as any)?.label ?? (opt as any)?.value ?? '').trim();
                if (!id || !label) return null;
                return (
                  <option key={`${id}-${label}`} value={id}>
                    {label}
                  </option>
                );
              })}
            </select>
            {teamHelperText && (
              <div id="preferred-team-helper" className="uimodal-help">
                {teamHelperText}
              </div>
            )}
          </div>
        </div>

        <div className="uimodal-grid">
          <div className="uimodal-field">
            <label className="uimodal-label">{'\u59d3'}</label>
            <input
              className="uimodal-input"
              type="text"
              value={lastName}
              onChange={(e) => {
                markFormEdited();
                setLastName(e.target.value);
              }}
              placeholder="山田"
              required
              aria-required="true"
            />
          </div>
          <div className="uimodal-field">
            <label className="uimodal-label">名</label>
            <input
              className="uimodal-input"
              type="text"
              value={firstName}
              onChange={(e) => {
                markFormEdited();
                setFirstName(e.target.value);
              }}
              placeholder="太郎"
              required
              aria-required="true"
            />
          </div>
        </div>

        {/* ふりがな（任意だが、入っていればひらがなチェック） */}
        <div className="uimodal-grid">
          <div className="uimodal-field">
            <label className="uimodal-label">姓（ふりがな）</label>
            <input
              className="uimodal-input"
              type="text"
              value={lastNameKana}
              onChange={(e) => {
                markFormEdited();
                setLastNameKana(e.target.value);
              }}
              placeholder="やまだ"
              aria-label="姓のふりがな"
            />
          </div>
          <div className="uimodal-field">
            <label className="uimodal-label">名（ふりがな）</label>
            <input
              className="uimodal-input"
              type="text"
              value={firstNameKana}
              onChange={(e) => {
                markFormEdited();
                setFirstNameKana(e.target.value);
              }}
              placeholder="じろう"
              aria-label="名のふりがな"
            />
          </div>
        </div>

        {error && (
          <div className="uimodal-error" role="alert">
            {error}
          </div>
        )}

        {/* 他課公開ダイアログ */}
        <PublishScopeDialog
          open={publishDialogOpen}
          onClose={() => setPublishDialogOpen(false)}
          targetLabel="下書きの他課公開"
          initialPublic={draftPublic}
          onApply={(next) => {
            markFormEdited();
            setDraftPublic(next);
          }}
        />

        <div className="uimodal-actions">
          {showCancelButton && (
            <button
              className="uimodal-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
          )}
          <button
            className="uimodal-ok"
            onClick={onClickOk}
            disabled={!canSubmit}
          >
            {submitting ? '送信中...' : editMode ? '更新' : '登録'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const UserInfoModalController: React.FC = () => {
  const authUser = useUser();
  const hasCheckedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [lockUntilSaved, setLockUntilSaved] = useState(false);
  const [forceUpdateOnSave, setForceUpdateOnSave] = useState(false);
  const [initialUserInfo, setInitialUserInfo] =
    useState<UserInfoApiResponse | null>(null);
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();
  const { setProfile } = useUserProfile();
  const demoLoginId = process.env.REACT_APP_INITIAL_DEMO_USER;
  const loginId = String(
    (authUser as any)?.signInDetails?.loginId ?? '',
  ).trim();
  const isDemoTarget = Boolean(
    demoLoginId && loginId && demoLoginId === loginId,
  );

  useEffect(() => {
    if (!authUser) return;
    if (hasCheckedRef.current) return;

    hasCheckedRef.current = true;

    // デモ対象ユーザーは常にモーダル表示し、取得処理はスキップ
    if (isDemoTarget) {
      setInitialUserInfo(null);
      setLockUntilSaved(true);
      setForceUpdateOnSave(true);
      setEditMode(false);
      setOpen(true);
      return;
    }

    (async () => {
      try {
        const authSnapshot = await getCognitoAuthSnapshot();
        const email = authSnapshot.email;
        if (!email) return;

        const groupArr = authSnapshot.groups;
        const cognitoGroupIsAdmin = groupArr.some((g) =>
          g.includes('-reps'),
        );
        let authorityVal: number | null = cognitoGroupIsAdmin ? 1 : null;
        const res = await shareInFlightRequest(
          `get-user-info:${email.trim().toLowerCase()}`,
          () => apiClient.post('/get_user_info', { mail_address: email }),
        );

        if (res.status === 200) {
          const data: any = res.data;
          setInitialUserInfo(data);
          // 取得したユーザー情報をアプリ全体で参照可能にする
          const lastName = String(
            data?.last_name ?? data?.lastName ?? '',
          ).trim();
          const firstName = String(
            data?.first_name ?? data?.firstName ?? '',
          ).trim();
          const lastNameKana = String(
            data?.last_name_kana ?? data?.lastNameKana ?? '',
          ).trim();
          const firstNameKana = String(
            data?.first_name_kana ?? data?.firstNameKana ?? '',
          ).trim();
          const department = String(data?.department ?? '').trim();
          const team = String(data?.team ?? '').trim();
          const preferredTeamIdRaw = String(
            data?.preferred_team_id ?? data?.preferredTeamId ?? '',
          ).trim();
          const hasPreferredTeamId = hasValidPreferredTeamId(preferredTeamIdRaw);
          const preferredTeamId = hasPreferredTeamId
            ? preferredTeamIdRaw
            : '';
          const preferredTeamLabel = String(
            data?.preferred_team_label ?? data?.preferredTeamLabel ?? '',
          ).trim();
          const preferredTeamUpdatedAt = String(
            data?.preferred_team_updated_at ??
              data?.preferredTeamUpdatedAt ??
              '',
          ).trim();
          authorityVal = resolveAuthority(data, cognitoGroupIsAdmin);
          setProfile({
            lastName,
            firstName,
            lastNameKana,
            firstNameKana,
            department,
            team,
            preferredTeamId,
            preferredTeamLabel,
            preferredTeamUpdatedAt,
            authority: authorityVal,
          });

          if (!hasPreferredTeamId) {
            setForceUpdateOnSave(true);
            setLockUntilSaved(true);
            setEditMode(true);
            setOpen(true);
          }
        }
      } catch (e: any) {
        // フェッチ失敗時でも、Cognitoグループから判定できる権限は即時反映してガードの誤判定を防ぐ
        try {
          const groupArr = (await getCognitoAuthSnapshot()).groups;
          const authorityVal: number | null = groupArr.some(g => g.includes('-reps')) ? 1 : null;
          // 既存プロフィールを維持しつつ authority のみ更新（loaded も立つ）
          setProfile({ authority: authorityVal });
        } catch {}

        const status = e?.response?.status;
        if (status === 404) {
          console.log('Not found');
          setInitialUserInfo(null);
          // 初回プロフィール未登録の場合はモーダルを開いて入力を促す
          setForceUpdateOnSave(false);
          setLockUntilSaved(true);
          setEditMode(false);
          setOpen(true);
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, isDemoTarget]);

  // ヘッダー等からの明示オープン要求を受け取る（グローバルイベント）
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent)?.detail;
      setInitialUserInfo(null);
      setEditMode(Boolean(d?.editMode));
      setLockUntilSaved(false);
      setForceUpdateOnSave(false);
      setOpen(true);
    };
    window.addEventListener(
      'open-user-info-modal',
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        'open-user-info-modal',
        handler as EventListener,
      );
    };
  }, []);

  return (
    <UserInfoModal
      isOpen={open}
      onClose={() => {
        if (lockUntilSaved) return;
        setInitialUserInfo(null);
        setOpen(false);
      }}
      onSaved={() => {
        setInitialUserInfo(null);
        setLockUntilSaved(false);
        setForceUpdateOnSave(false);
        setEditMode(false);
        setOpen(false);
      }}
      divisions={(divisionOptions ?? []).map((opt) => ({
        value: String(opt.value ?? ''),
        label: String(opt.label ?? ''),
        department_id: Number((opt as any)?.department_id ?? 0) || undefined,
      }))}
      teams={(teamOptions ?? []).map((opt) => ({
        value: String(opt.value ?? ''),
        label: String(opt.label ?? ''),
        team_id: Number((opt as any)?.team_id ?? 0) || undefined,
        department_id: Number((opt as any)?.department_id ?? 0) || undefined,
      }))}
      forceUpdateEndpoint={forceUpdateOnSave}
      editMode={editMode}
      lockUntilSaved={lockUntilSaved}
      initialUserInfo={initialUserInfo}
      onAllowCancelAfterSubmitFailure={() => setLockUntilSaved(false)}
    />
  );
};

export default UserInfoModal;
